import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, Download, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";
import { Card } from "@/components/ui/Card";
import { FormMessage, type Feedback } from "@/components/ui/FormMessage";
import type { GenericResponse, UpdateCheckResponse, UpdatePhase } from "@/lib/types";

const STATUS_POLL_MS = 2000;
// Wie lange wir während eines Recreates Verbindungsabbrüche tolerieren,
// bevor wir aufgeben. Der Container kommt typischerweise in <30 s zurück.
const STATUS_TOLERATE_LOSS_MS = 90_000;
// Erfolgsfall: nach kurzer Anzeige neu laden, damit `current` aktualisiert wird.
const SUCCESS_RELOAD_MS = 1500;
const PROGRESS_TICK_MS = 400;

/**
 * Pro Phase: Ziel-% (Asymptote) und erwartete Dauer in Sekunden bis ~Ziel.
 * Echte Prozente kennen wir nicht (Docker liefert keine), aber das Mapping
 * fühlt sich passend an: pulling ist der längste Schritt, recreating der
 * zweitlängste, der Server-Restart ist meist <30s.
 */
const PHASE_PROGRESS: Record<"pulling" | "recreating" | "unreachable", { target: number; expectedSec: number }> = {
  pulling: { target: 55, expectedSec: 45 },
  recreating: { target: 85, expectedSec: 25 },
  unreachable: { target: 95, expectedSec: 20 },
};

function phaseKey(p: UpdatePhase | null): string {
  if (!p) return "none";
  if (p.phase === "ok" || p.phase === "failed") return `${p.phase}-${p.finished_at}`;
  return p.phase;
}

function easeOutCubic(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - c, 3);
}

export function UpdatesPage() {
  const [check, setCheck] = useState<UpdateCheckResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [phase, setPhase] = useState<UpdatePhase | null>(null);
  const [progress, setProgress] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const autoTriggerHandled = useRef(false);
  const pollTimerRef = useRef<number | null>(null);
  const lossStartRef = useRef<number | null>(null);
  const progressTimerRef = useRef<number | null>(null);
  const phaseStartRef = useRef<{ key: string; ts: number; startProgress: number }>({
    key: "none",
    ts: 0,
    startProgress: 0,
  });

  const reload = useCallback(async (manual = false) => {
    if (manual) setChecking(true);
    else setLoading(true);
    try {
      const c = await api.get<UpdateCheckResponse>("/api/update/check");
      setCheck(c);
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Update-Check fehlgeschlagen") });
    } finally {
      setChecking(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const stopProgressAnim = useCallback(() => {
    if (progressTimerRef.current) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    lossStartRef.current = null;
  }, []);

  // Status-Polling: ein einzelner Tick.
  const pollOnce = useCallback(async () => {
    try {
      const s = await api.get<UpdatePhase>("/api/update/status");
      lossStartRef.current = null;
      setPhase(s);
      if (s.phase === "ok") {
        stopPolling();
        setProgress(100);
        stopProgressAnim();
        // Kurz die Erfolgsmeldung stehen lassen, dann reloaden — das holt
        // die neue Version sauber und der Banner verschwindet.
        window.setTimeout(() => window.location.reload(), SUCCESS_RELOAD_MS);
      } else if (s.phase === "failed" || s.phase === "unavailable" || s.phase === "error") {
        stopPolling();
        stopProgressAnim();
      }
    } catch {
      // Während Recreate ist der log-server selbst kurz weg — das ist
      // erwartbar. Wir markieren den Beginn und brechen erst nach einem
      // großzügigen Timeout endgültig ab.
      const now = Date.now();
      if (lossStartRef.current == null) lossStartRef.current = now;
      setPhase({ phase: "unreachable", reason: t("Server antwortet gerade nicht (Restart läuft?)") });
      if (now - lossStartRef.current > STATUS_TOLERATE_LOSS_MS) {
        stopPolling();
        stopProgressAnim();
      }
    }
  }, [stopPolling, stopProgressAnim]);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    void pollOnce();
    pollTimerRef.current = window.setInterval(() => void pollOnce(), STATUS_POLL_MS);
  }, [pollOnce]);

  // ── Progress-Animation ────────────────────────────────────────────
  // Bei jedem Phasen-Wechsel den Start-Anker setzen; ein Timer animiert
  // den Balken asymptotisch Richtung Ziel-%. Ist monoton — Progress fällt
  // nie zurück, auch wenn die Phase rückwärts wechseln würde.
  useEffect(() => {
    const key = phaseKey(phase);
    if (phaseStartRef.current.key !== key) {
      phaseStartRef.current = {
        key,
        ts: Date.now(),
        startProgress: progress,
      };
    }

    if (!phase) {
      stopProgressAnim();
      return;
    }
    if (phase.phase === "ok") {
      setProgress(100);
      stopProgressAnim();
      return;
    }
    if (phase.phase === "failed" || phase.phase === "unavailable" || phase.phase === "error") {
      stopProgressAnim();
      return;
    }
    if (phase.phase === "idle") return;

    const conf = PHASE_PROGRESS[phase.phase];
    if (!conf) return;

    if (progressTimerRef.current) return; // läuft schon
    progressTimerRef.current = window.setInterval(() => {
      const anchor = phaseStartRef.current;
      const elapsedSec = (Date.now() - anchor.ts) / 1000;
      const eased = easeOutCubic(elapsedSec / conf.expectedSec);
      const next = anchor.startProgress + (conf.target - anchor.startProgress) * eased;
      setProgress((cur) => Math.max(cur, Math.min(conf.target, next)));
    }, PROGRESS_TICK_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(
    () => () => {
      stopPolling();
      stopProgressAnim();
    },
    [stopPolling, stopProgressAnim],
  );

  const onTrigger = useCallback(async () => {
    if (!confirm(t("Update jetzt installieren? Der Server startet sich anschließend neu."))) return;
    setTriggering(true);
    setFeedback(null);
    setPhase(null);
    setProgress(0);
    try {
      const resp = await api.post<GenericResponse>("/api/update/trigger", {});
      if (resp.success) {
        startPolling();
      } else {
        setFeedback({ kind: "error", text: resp.message });
      }
    } catch (e) {
      setFeedback({ kind: "error", text: e instanceof Error ? e.message : t("Trigger fehlgeschlagen") });
    } finally {
      setTriggering(false);
    }
  }, [startPolling]);

  // Auto-Trigger, wenn vom Banner mit `state.autoTrigger` hierher navigiert.
  // Wir warten den ersten Check ab, damit `trigger_enabled` & `update_available`
  // bekannt sind. Der Ref-Guard schützt vor Doppel-Triggern bei Re-Render.
  useEffect(() => {
    if (autoTriggerHandled.current) return;
    const state = location.state as { autoTrigger?: boolean } | null;
    if (!state?.autoTrigger) return;
    if (loading || triggering) return;
    if (!check?.update_available || !check.trigger_enabled) return;
    autoTriggerHandled.current = true;
    // History-State löschen, damit ein Reload nicht erneut triggert.
    navigate(location.pathname, { replace: true });
    void onTrigger();
  }, [location.state, location.pathname, loading, triggering, check, navigate, onTrigger]);

  // Wenn beim Page-Mount der Updater bereits in einem laufenden Update
  // ist (z.B. User hat F5 gedrückt während Update lief), Polling sofort
  // wieder aufnehmen — sonst sitzt er auf einer toten Seite.
  useEffect(() => {
    let cancelled = false;
    void api
      .get<UpdatePhase>("/api/update/status")
      .then((s) => {
        if (cancelled) return;
        if (s.phase === "pulling" || s.phase === "recreating") {
          setPhase(s);
          startPolling();
        }
      })
      .catch(() => {
        // Wenn der Status nicht erreichbar ist, lassen wir's — kein Update läuft.
      });
    return () => {
      cancelled = true;
    };
    // Bewusst nur beim Mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const running = isPhaseRunning(phase);

  return (
    <div className="flex flex-col gap-4 p-6">
      <FormMessage feedback={feedback} />

      {phase ? (
        <div className="flex flex-col gap-2">
          <PhaseBanner phase={phase} />
          {running || phase.phase === "ok" ? <ProgressBar value={progress} indeterminate={phase.phase === "unreachable"} /> : null}
        </div>
      ) : null}

      <Card
        title={t("Version")}
        actions={
          <button
            type="button"
            onClick={() => reload(true)}
            disabled={checking}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-60"
          >
            <RefreshCw className={"h-3 w-3 " + (checking ? "animate-spin" : "")} />
            {checking ? t("Prüfe…") : t("Erneut prüfen")}
          </button>
        }
      >
        {loading ? (
          <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
        ) : check ? (
          <>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-xs">
              <dt className="text-muted-foreground">{t("Installiert")}</dt>
              <dd className="font-mono">v{check.current}</dd>
              <dt className="text-muted-foreground">{t("Verfügbar")}</dt>
              <dd className="font-mono">
                {check.latest ? `v${check.latest}` : <span className="text-muted-foreground">{t("unbekannt")}</span>}
              </dd>
            </dl>

            {check.error ? (
              <p className="mt-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
                {t("Manifest konnte nicht geladen werden: {error}", { error: check.error })}
              </p>
            ) : null}

            {check.update_available ? (
              <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2">
                <div className="flex items-center gap-2 text-xs text-warning">
                  <Download className="h-3.5 w-3.5" />
                  {t("Update auf v{version} verfügbar.", { version: check.latest ?? "" })}
                </div>
                {check.trigger_enabled ? (
                  <button
                    type="button"
                    onClick={onTrigger}
                    disabled={triggering || running}
                    className="inline-flex h-7 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground disabled:opacity-60"
                  >
                    {triggering ? t("Starte…") : t("Installieren")}
                  </button>
                ) : (
                  <span className="text-[11px] text-muted-foreground">
                    {t("Updater nicht konfiguriert")}
                  </span>
                )}
              </div>
            ) : (
              <div className="mt-4 flex items-center gap-2 rounded-md border border-success/40 bg-success/10 px-3 py-2 text-xs text-success">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t("Aktuelle Version installiert.")}
              </div>
            )}
          </>
        ) : null}
      </Card>

      <Card title={t("Auto-Update via Sidecar")}>
        <p className="text-xs text-muted-foreground">
          {t("Leif aktualisiert sich über den")}{" "}
          <span className="font-mono text-foreground">log-updater</span>
          {t("-Sidecar. Voraussetzung: die Umgebungsvariablen")}{" "}
          <span className="font-mono">UPDATER_URL</span> {t("und")}{" "}
          <span className="font-mono">UPDATE_TOKEN</span>{" "}
          {t(
            "sind gesetzt. Andernfalls bleibt der Install-Button ausgeblendet — Updates müssen dann manuell über die Container-Plattform eingespielt werden.",
          )}
        </p>
      </Card>
    </div>
  );
}

function isPhaseRunning(p: UpdatePhase | null): boolean {
  return p?.phase === "pulling" || p?.phase === "recreating" || p?.phase === "unreachable";
}

function ProgressBar({ value, indeterminate }: { value: number; indeterminate?: boolean }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={
          "h-full bg-primary transition-[width] duration-500 ease-out " +
          (indeterminate ? "animate-pulse" : "")
        }
        style={{ width: `${v}%` }}
        aria-valuenow={Math.round(v)}
        aria-valuemin={0}
        aria-valuemax={100}
        role="progressbar"
      />
    </div>
  );
}

function PhaseBanner({ phase }: { phase: UpdatePhase }) {
  switch (phase.phase) {
    case "pulling":
      return (
        <StatusRow tone="info" Icon={Loader2} spin>
          {t("Image wird heruntergeladen…")}
        </StatusRow>
      );
    case "recreating":
      return (
        <StatusRow tone="info" Icon={Loader2} spin>
          {t("Container wird neu erstellt — der Server ist gleich wieder erreichbar.")}
        </StatusRow>
      );
    case "unreachable":
      return (
        <StatusRow tone="info" Icon={Loader2} spin>
          {phase.reason}
        </StatusRow>
      );
    case "ok":
      return (
        <StatusRow tone="success" Icon={CheckCircle2}>
          {t("Update erfolgreich — Seite wird neu geladen…")}
        </StatusRow>
      );
    case "failed":
      return (
        <StatusRow tone="error" Icon={AlertCircle}>
          {t("Update fehlgeschlagen:")} <span className="font-mono">{phase.reason}</span>
        </StatusRow>
      );
    case "unavailable":
      return (
        <StatusRow tone="error" Icon={AlertCircle}>
          {t("Updater nicht konfiguriert: {reason}", { reason: phase.reason })}
        </StatusRow>
      );
    case "error":
      return (
        <StatusRow tone="error" Icon={AlertCircle}>
          {t("Statusabfrage fehlgeschlagen: {reason}", { reason: phase.reason })}
        </StatusRow>
      );
    case "idle":
      return null;
  }
}

function StatusRow({
  tone,
  Icon,
  spin = false,
  children,
}: {
  tone: "info" | "success" | "error";
  Icon: typeof Loader2;
  spin?: boolean;
  children: React.ReactNode;
}) {
  const cls =
    tone === "success"
      ? "border-success/40 bg-success/10 text-success"
      : tone === "error"
        ? "border-destructive/40 bg-destructive/10 text-destructive"
        : "border-border bg-accent text-accent-foreground";
  return (
    <div className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${cls}`}>
      <Icon className={"h-3.5 w-3.5 shrink-0 " + (spin ? "animate-spin" : "")} />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}
